/**
 * Create Invitation – Step 3: Confirm send content (送信内容確認).
 *
 * Customize email: recipient dropdown (from Step 1), deadline date picker,
 * subject (default + editable), mail body (placeholders + editable via Edit button).
 */
import React, { useState, useMemo, useEffect } from 'react';
import type { InvitationDeliveryMode } from '../../../../../../services/invitationSendApi';
import { fetchMailTemplate } from '../../../../../../services/mailTemplateApi';
import Roadmap from '../Roadmap';
import { Participant } from '../step1/types';
import { Recipient } from './types';
import './CreateInvitationStep3.css';
import backButtonIcon from '../../../../../../assests/icons/back-button.png';
import editIcon from '../../../../../../assests/icons/edit_icon.png';

/** Fallback if GET /api/mail-templates/INVITATION fails (matches backend seed). */
const FALLBACK_SUBJECT = '【入社手続きのご案内】 {受信者名}様 / 提出期限: {締切日}';

const FALLBACK_BODY = `西行土木株式会社でございます。
入社手続きについてご案内いたします。

以下の期日までに、Webシステムへのログインおよび必要手続きの完了をお願いいたします。

【ログイン情報】
メールアドレス：{受信者メールアドレス}
初期パスワード：{パスワード}

【Webシステム】
下記リンクよりアクセスしてください。
▶ システムはこちら：{WebサイトURL}

【ログイン方法・初期設定マニュアル】
初回ログイン方法およびパスワード変更手順については、以下をご確認ください。
▶ マニュアルはこちら：{マニュアルURL}

提出期限：{締切日}

※初回ログイン後は、必ずパスワードの変更をお願いいたします。

ご不明な点がございましたら、下記までお問い合わせください。

{送信者名} 
{送信者部署名}
メール：{送信者メールアドレス}
電話：{送信者電話番号}`;

const SUBJECT_PLACEHOLDER_WARNING = '以下のプレースホルダーは変更・削除しないでください: {受信者名}, {締切日}';
const BODY_PLACEHOLDER_WARNING = '以下のプレースホルダーは変更・削除しないでください: {受信者メールアドレス}, {パスワード}, {締切日}';

/** Build recipients from Step 1 participants (placeholder email for display). */
function toRecipients(participants: Participant[]): Recipient[] {
  return participants;
}

export interface CreateInvitationStep3Props {
  /** Recipients selected in Step 1 (id, name; email can be added by parent) */
  selectedParticipants?: Participant[];
  /** Callback when header back arrow is clicked (go back to Step 2) */
  onBackToStep2?: () => void;
  /** Callback when footer 戻る is clicked (return to 招待管理) */
  onBack?: () => void;
  /** Callback when 送信する / 発行する is clicked */
  onSend?: (payload: {
    subject: string;
    body: string;
    deadline: string;
    recipientIds: string[];
    deliveryMode: InvitationDeliveryMode;
  }) => void;

  /** Whether the invitation is being sent */
  sending?: boolean;
}

const CreateInvitationStep3: React.FC<CreateInvitationStep3Props> = ({
  selectedParticipants = [],
  onBackToStep2,
  onBack,
  onSend,
  sending = false,
}) => {
  const recipients = useMemo(() => toRecipients(selectedParticipants), [selectedParticipants]);

  const [selectedRecipientEmail, setSelectedRecipientEmail] = useState<string>(recipients[0]?.email ?? '');//dropdown
  const [deadline, setDeadline] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [storedDefaults, setStoredDefaults] = useState<{ subject: string; body: string }>({
    subject: FALLBACK_SUBJECT,
    body: FALLBACK_BODY,
  });
  const [subject, setSubject] = useState(FALLBACK_SUBJECT);
  const [body, setBody] = useState(FALLBACK_BODY);

  useEffect(() => {
    let cancelled = false;
    fetchMailTemplate('INVITATION')
      .then((row) => {
        if (cancelled) return;
        setStoredDefaults({ subject: row.subject, body: row.body });
        setSubject(row.subject);
        setBody(row.body);
      })
      .catch(() => {
        if (cancelled) return;
        setStoredDefaults({ subject: FALLBACK_SUBJECT, body: FALLBACK_BODY });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const [subjectEditable, setSubjectEditable] = useState(false);
  const [bodyEditable, setBodyEditable] = useState(false);
  /** 'subject' | 'body' when edit-choice modal is open; null when closed */
  const [editChoiceTarget, setEditChoiceTarget] = useState<'subject' | 'body' | null>(null);
  /** Modal step: choice (default vs edit) or warning (placeholder notice before editing) */
  const [editChoicePhase, setEditChoicePhase] = useState<'choice' | 'warning'>('choice');
  const [deliveryMode, setDeliveryMode] = useState<InvitationDeliveryMode>('EMAIL');

  const handleEditSubjectClick = () => {
    if (subjectEditable) {
      setSubjectEditable(false);
      return;
    }
    setEditChoiceTarget('subject');
    setEditChoicePhase('choice');
  };

  const handleEditBodyClick = () => {
    if (bodyEditable) {
      setBodyEditable(false);
      return;
    }
    setEditChoiceTarget('body');
    setEditChoicePhase('choice');
  };

  const handleEditChoiceSetDefault = () => {
    if (editChoiceTarget === 'subject') {
      setSubject(storedDefaults.subject);
    } else if (editChoiceTarget === 'body') {
      setBody(storedDefaults.body);
    }
    setEditChoiceTarget(null);
  };

  const handleEditChoiceEditContent = () => {
    setEditChoicePhase('warning');
  };

  const handleEditChoiceStartEditing = () => {
    if (editChoiceTarget === 'subject') {
      setSubjectEditable(true);
    } else if (editChoiceTarget === 'body') {
      setBodyEditable(true);
    }
    setEditChoiceTarget(null);
  };

  const handleEditChoiceClose = () => {
    setEditChoiceTarget(null);
    setEditChoicePhase('choice');
  };

  const handleSend = () => {
    onSend?.({
      subject,
      body,
      deadline,
      recipientIds: recipients.map((r) => r.email),
      deliveryMode,
    });
  };

  const extraCount = recipients.length > 1 ? `+${recipients.length - 1}` : '';
  const isManualDelivery = deliveryMode === 'MANUAL';

  return (
    <div className="create-invitation-step3">
      <div className="create-invitation-step3__header">
        <button
          className="create-invitation-step3__back-btn"
          onClick={onBackToStep2}
          aria-label="前のステップへ"
        >
          <img src={backButtonIcon} alt="" className="create-invitation-step3__back-icon" aria-hidden />
        </button>
        <h1 className="create-invitation-step3__title">招待を作成</h1>
      </div>

      <div className="create-invitation-step3__roadmap">
        <Roadmap currentStep={3} />
      </div>

      <div className="create-invitation-step3__content">
        <div className="create-invitation-step3__delivery-row">
          <span className="create-invitation-step3__label">配信方法:</span>
          <label className="create-invitation-step3__delivery-option">
            <input
              type="radio"
              name="invitation-delivery-mode"
              value="EMAIL"
              checked={deliveryMode === 'EMAIL'}
              onChange={() => setDeliveryMode('EMAIL')}
            />
            メールで送信
          </label>
          <label className="create-invitation-step3__delivery-option">
            <input
              type="radio"
              name="invitation-delivery-mode"
              value="MANUAL"
              checked={deliveryMode === 'MANUAL'}
              onChange={() => setDeliveryMode('MANUAL')}
            />
            LINE等（コピーして手動送信）
          </label>
        </div>
        {isManualDelivery && (
          <p className="create-invitation-step3__delivery-hint">
            メールは送信しません。発行後に表示される画面から、従業員ごとの本文をコピーして LINE 等で送ってください。
          </p>
        )}
        {/* 宛先リスト and 提出期限 on same row */}
        <div className="create-invitation-step3__top-row">
          <div className="create-invitation-step3__row">
            <label className="create-invitation-step3__label">宛先リスト:</label>
            <div className="create-invitation-step3__recipient-wrap">
              <select
                className="create-invitation-step3__recipient-select"
                value={selectedRecipientEmail}
                onChange={(e) => setSelectedRecipientEmail(e.target.value)}
                aria-label="宛先を選択"
              >
                {recipients.map((r) => (
                  <option key={r.email} value={r.email}>
                    {r.name} : {r.email}
                  </option>
                ))}
              </select>
              {extraCount && <span className="create-invitation-step3__extra-count">{extraCount}</span>}
            </div>
          </div>
          <div className="create-invitation-step3__row">
            <label className="create-invitation-step3__label">提出期限:</label>
            <div className="create-invitation-step3__date-wrap">
              <input
                type="date"
                className="create-invitation-step3__date-input"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                aria-label="提出期限"
              />
              
            </div>
          </div>
        </div>

        {/* 件名 - label, input and Edit button on same row */}
        <div className="create-invitation-step3__subject-row">
          <label className="create-invitation-step3__label">件名:</label>
          <input
            type="text"
            className="create-invitation-step3__subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            readOnly={!subjectEditable}
            aria-label="件名"
          />
          <button
            type="button"
            className="create-invitation-step3__edit-btn"
            onClick={handleEditSubjectClick}
            aria-label={subjectEditable ? '編集を完了' : '件名を編集'}
          >
            {subjectEditable ? '完了' : '編集'}
          </button>
        </div>

        {/* メール本文 */}
        <div className="create-invitation-step3__field create-invitation-step3__field--body">
          <div className="create-invitation-step3__body-header">
            <label className="create-invitation-step3__label">メール本文:</label>
            <button
              type="button"
              className="create-invitation-step3__edit-btn create-invitation-step3__edit-btn--body"
              onClick={handleEditBodyClick}
              aria-label={bodyEditable ? '編集を完了' : '本文を編集'}
            >
              <img src={editIcon} alt="" className="create-invitation-step3__edit-icon" aria-hidden />
              {bodyEditable ? '完了' : '編集'}
            </button>
          </div>
          <textarea
            className="create-invitation-step3__body-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            readOnly={!bodyEditable}
            rows={14}
            aria-label="メール本文"
          />
        </div>
      </div>

      <div className="create-invitation-step3__footer">
        <button type="button" className="create-invitation-step3__back-footer-btn" onClick={onBack}>
          招待へ
        </button>
        <button type="button" className="create-invitation-step3__send-btn" onClick={handleSend} disabled={sending}>
          {sending
            ? isManualDelivery
              ? '発行中...'
              : '送信中...'
            : isManualDelivery
              ? '招待を発行する'
              : '送信する'}
        </button>
      </div>

      {/* Edit choice modal: set to default or edit content; then optional warning step */}
      {editChoiceTarget && (
        <div className="create-invitation-step3__modal-overlay" onClick={handleEditChoiceClose}>
          <div className="create-invitation-step3__modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="create-invitation-step3__modal-title">
              {editChoiceTarget === 'subject' ? '件名' : 'メール本文'}
            </h3>
            {editChoicePhase === 'choice' ? (
              <>
                <p className="create-invitation-step3__modal-text">
                  デフォルトに戻しますか、それとも内容を編集しますか？
                </p>
                <div className="create-invitation-step3__modal-actions">
                  <button
                    type="button"
                    className="create-invitation-step3__modal-btn create-invitation-step3__modal-btn--default"
                    onClick={handleEditChoiceSetDefault}
                  >
                    デフォルトに戻す
                  </button>
                  <button
                    type="button"
                    className="create-invitation-step3__modal-btn create-invitation-step3__modal-btn--edit"
                    onClick={handleEditChoiceEditContent}
                  >
                    内容を編集
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="create-invitation-step3__modal-text create-invitation-step3__modal-text--warning">
                  {editChoiceTarget === 'subject' ? SUBJECT_PLACEHOLDER_WARNING : BODY_PLACEHOLDER_WARNING}
                </p>
                <div className="create-invitation-step3__modal-actions">
                  <button
                    type="button"
                    className="create-invitation-step3__modal-btn create-invitation-step3__modal-btn--edit"
                    onClick={handleEditChoiceStartEditing}
                  >
                    編集を開始
                  </button>
                </div>
              </>
            )}
            <button
              type="button"
              className="create-invitation-step3__modal-close"
              onClick={handleEditChoiceClose}
              aria-label="閉じる"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateInvitationStep3;
